/**
 * 10.8H — CONTA POR E-MAIL (Supabase Auth). Permite "promover" a sessão ANÔNIMA para uma conta
 * de verdade (e-mail + senha) MANTENDO o mesmo user_id — então o save/perfil na nuvem seguem
 * ligados, e dá pra ENTRAR com a mesma conta em outro aparelho e puxar o progresso.
 *
 * Tolerante: sem internet/config, os métodos devolvem {ok:false, msg}. A UI mostra a mensagem.
 * Obs.: se "Confirm email" estiver LIGADO no Supabase, a conta só ativa após confirmar por e-mail.
 */
import { supabase } from "./supabase";

export interface AuthState {
  email: string | null;
  anonymous: boolean;
  signedIn: boolean;
  /** 10.9IC — "google" | "facebook" | "email" | null (como a conta foi criada/vinculada). */
  provider: string | null;
  /** 10.9IC — nome vindo do provedor social (Google/Facebook), quando houver. */
  name: string | null;
}

/** 10.9IC — provedores sociais suportados. O "ID do Void" é o e-mail+senha (createAccount/login). */
export type SocialProvider = "google" | "facebook";

export async function authState(): Promise<AuthState> {
  try {
    const { data } = await supabase.auth.getSession();
    const u = data.session?.user;
    const meta = (u?.user_metadata ?? {}) as { full_name?: string; name?: string };
    // `identities` lista os vínculos (google/facebook/email); o último é o mais recente.
    const ident = u?.identities?.[u.identities.length - 1]?.provider ?? null;
    return {
      email: u?.email ?? null,
      anonymous: u?.is_anonymous === true,
      signedIn: !!u,
      provider: u?.is_anonymous ? null : ident,
      name: meta.full_name ?? meta.name ?? null,
    };
  } catch {
    return { email: null, anonymous: true, signedIn: false, provider: null, name: null };
  }
}

export interface AuthResult {
  ok: boolean;
  msg: string;
  needConfirm?: boolean;
}

/** Cria conta: se a sessão é anônima, PROMOVE (mantém user_id → progresso preservado). */
export async function createAccount(email: string, password: string): Promise<AuthResult> {
  const e = email.trim();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return { ok: false, msg: "E-mail inválido." };
  if (password.length < 6) return { ok: false, msg: "A senha precisa de 6+ caracteres." };
  try {
    const { data: sess } = await supabase.auth.getSession();
    if (sess.session?.user?.is_anonymous) {
      const { data, error } = await supabase.auth.updateUser({ email: e, password });
      if (error) return { ok: false, msg: traduz(error.message) };
      const needConfirm = !data.user?.email_confirmed_at;
      return { ok: true, needConfirm, msg: needConfirm ? "Conta criada! Confirme pelo link no seu e-mail." : "Conta criada e vinculada!" };
    }
    const { data, error } = await supabase.auth.signUp({ email: e, password });
    if (error) return { ok: false, msg: traduz(error.message) };
    const needConfirm = !data.session;
    return { ok: true, needConfirm, msg: needConfirm ? "Conta criada! Confirme pelo link no seu e-mail." : "Conta criada!" };
  } catch (err) {
    return { ok: false, msg: String(err) };
  }
}

/** Entra com e-mail+senha (troca a sessão para a conta → recarregar puxa o save dela). */
export async function login(email: string, password: string): Promise<AuthResult> {
  const e = email.trim();
  if (!e || !password) return { ok: false, msg: "Preencha e-mail e senha." };
  try {
    const { error } = await supabase.auth.signInWithPassword({ email: e, password });
    if (error) return { ok: false, msg: traduz(error.message) };
    return { ok: true, msg: "Entrou! Carregando seu progresso…" };
  } catch (err) {
    return { ok: false, msg: String(err) };
  }
}

/**
 * 10.9IC — LOGIN SOCIAL (Google / Facebook). Thiago: "conseguimos por a integração pra logar com
 * as contas do facebook, google, id do void".
 *
 * Igual ao e-mail: se a sessão atual é ANÔNIMA, VINCULA o provedor à mesma conta (`linkIdentity`)
 * pra não perder o progresso; se já é uma conta de verdade (ou não há sessão), faz login normal.
 * Os dois saem daqui por REDIRECT: o provedor volta pra `redirectTo` e o supabase-js lê a sessão
 * da URL (precisa de `detectSessionInUrl: true` — ver supabase.ts).
 */
/** 10.9IL — endereço só do app, registrado no AndroidManifest, para a aba do Chrome devolver o
 *  resultado do login PRA DENTRO do jogo. Precisa estar na lista de Redirect URLs do Supabase. */
const DEEP_LINK = "voidhunter://auth";

/** Rodando dentro do APK (Capacitor) e não no navegador? */
function noApp(): boolean {
  const c = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return typeof c?.isNativePlatform === "function" ? c.isNativePlatform() : false;
}

/**
 * 10.9IL — LOGIN SOCIAL DENTRO DO APK. Thiago: "quando coloca pra entrar com o google ele não abre
 * pra conectar uma pop-up e volta pro jogo, ele abre direto pro navegador".
 *
 * O que acontecia: o APK carrega a página publicada dentro da WebView do Android, e o
 * `signInWithOAuth` do Supabase navega a página inteira pro provedor. Qualquer navegação pra fora
 * do endereço do `server.url` é jogada pro NAVEGADOR DO SISTEMA pelo Capacitor — então o jogador
 * saía do jogo, logava no Chrome, e o redirect voltava… pro Chrome. O app continuava deslogado.
 *
 * E não adianta só liberar a navegação na WebView: o Google RECUSA de propósito fazer OAuth em
 * WebView embutida (`disallowed_useragent`). O caminho suportado é o mesmo de qualquer app nativo:
 * abrir uma ABA DO CHROME por cima do app (Custom Tab, que é um navegador de verdade) e voltar por
 * um endereço próprio do app. É isso que está aqui.
 *
 * No NAVEGADOR nada muda: segue o redirect normal da página.
 */
/**
 * 10.9IN — O APP SE ATUALIZA PELA WEB, MAS OS PLUGINS NATIVOS NAO. Erro relatado pelo Thiago:
 * `"browser" plugin is not implemented on android`.
 *
 * O APK carrega a pagina publicada, entao o codigo novo do 10.9IL chegou em TODO APK instalado —
 * inclusive nos anteriores a ele, que nao tem o plugin `@capacitor/browser` nem o intent-filter
 * `voidhunter://auth`. Resultado: quem estava num APK antigo passou a tomar um ERRO ao tocar em
 * Entrar com Google, pior do que o problema original.
 *
 * `isPluginAvailable("Browser")` resolve os dois de uma vez: o plugin e o intent-filter entraram no
 * MESMO build, entao a presenca de um garante a do outro. APK novo usa a aba do Chrome e o deep
 * link; APK antigo volta pro caminho de sempre (que nao e bom, mas funciona) e recebe um recado
 * pedindo pra atualizar.
 */
export async function signInWithProvider(provider: SocialProvider): Promise<AuthResult> {
  const app = noApp();
  const abaNativa = app && temBrowserNativo();
  const origem = typeof window !== "undefined" ? window.location.origin : undefined;
  const redirectTo = abaNativa ? DEEP_LINK : origem;
  try {
    const { data: sess } = await supabase.auth.getSession();
    const anonimo = sess.session?.user?.is_anonymous === true;
    // `skipBrowserRedirect` faz o Supabase DEVOLVER a URL em vez de navegar — é o que permite
    // abrir a aba do Chrome no lugar de arrastar a página inteira pra fora do app.
    const options = { redirectTo, skipBrowserRedirect: abaNativa };
    let r = anonimo
      ? await supabase.auth.linkIdentity({ provider, options })
      : await supabase.auth.signInWithOAuth({ provider, options });
    // 10.9IP — "identity is already linked to another user". Thiago viu isto ao entrar.
    // Acontece quando o aparelho esta numa sessao de CONVIDADO e a conta do Google escolhida JA
    // pertence a um Cacador (tipico de quem ja tinha entrado antes, ou de um segundo aparelho).
    // Vincular e impossivel, mas o que a pessoa quer e obvio: ENTRAR na conta dela. Antes o jogo
    // parava num beco e mandava "Use Sair e entre por ela" — um passo manual que ninguem descobre.
    // O progresso de convidado NAO se perde: no boot o save local funde por personagem com o da
    // conta (mergeChars fica com o mais avancado de cada classe).
    if (anonimo && r.error && jaVinculada(r.error.message)) {
      r = await supabase.auth.signInWithOAuth({ provider, options });
    }
    if (r.error) return { ok: false, msg: traduz(r.error.message) };
    if (!abaNativa) {
      // A página sai pro provedor sozinha (web, ou APK antigo sem o plugin).
      return app
        ? { ok: false, msg: "Esta versão do app abre o login no navegador e não volta sozinha. Instale o APK novo." }
        : { ok: true, msg: "Abrindo o login…" };
    }
    const url = r.data?.url;
    if (!url) return { ok: false, msg: "O servidor não devolveu o endereço do login." };
    await abrirLoginNoApp(url);
    return { ok: true, msg: "Abrindo o login…" };
  } catch (err) {
    return { ok: false, msg: String(err) };
  }
}

/** A conta social ja pertence a OUTRO usuario? (o Supabase tem mais de um texto pra isso) */
function jaVinculada(msg: string): boolean {
  const m = msg.toLowerCase();
  return m.includes("already linked") || m.includes("identity already exists") || m.includes("identity_already_exists");
}

/** O APK tem o plugin da aba do Chrome? Se não tem, também não tem o deep link: é build antigo. */
function temBrowserNativo(): boolean {
  const c = (window as unknown as { Capacitor?: { isPluginAvailable?: (n: string) => boolean } }).Capacitor;
  try {
    return typeof c?.isPluginAvailable === "function" ? c.isPluginAvailable("Browser") : false;
  } catch {
    return false;
  }
}

/** Abre a aba do Chrome e fica de olho na volta pelo deep link. */
async function abrirLoginNoApp(url: string): Promise<void> {
  const [{ Browser }, { App }] = await Promise.all([import("@capacitor/browser"), import("@capacitor/app")]);
  // Um ouvinte por vez: se o jogador tentar de novo, o anterior é descartado.
  await pararEscuta();
  voltou = false;
  const h = await App.addListener("appUrlOpen", (ev: { url: string }) => {
    void concluirLogin(ev.url, Browser);
  });
  // 10.9IM — SE A ABA FECHAR SEM VOLTAR PRO APP, dizer POR QUE. Sem isto o jogador fica olhando
  // "Abrindo o login…" pra sempre e não há como saber o que falhou. O caso real: se
  // `voidhunter://auth` não estiver nas Redirect URLs do Supabase, ele manda a volta pro Site URL
  // — a aba carrega o site e nunca chama o app. Fechar a aba à mão cai aqui também, e aí a
  // mensagem é só "login cancelado".
  const f = await Browser.addListener("browserFinished", () => {
    if (voltou) return;
    window.dispatchEvent(new CustomEvent("vh-auth", { detail: { ok: false, msg: FALHOU_VOLTAR } }));
    void pararEscuta();
  });
  ouvinteDeVolta = { remove: async () => { await h.remove(); await f.remove(); } };
  try {
    await Browser.open({ url, presentationStyle: "popover" });
  } catch (err) {
    // Cinto e suspensorio: se o plugin existir mas a abertura falhar, nao deixa o jogador
    // preso em "Abrindo o login…" sem explicacao.
    await pararEscuta();
    window.dispatchEvent(new CustomEvent("vh-auth", { detail: { ok: false, msg: `Não consegui abrir o login: ${String(err)}` } }));
  }
}

const FALHOU_VOLTAR =
  "O login não voltou pro app. Se você concluiu no Google, falta liberar voidhunter://auth nas Redirect URLs do Supabase.";

/** Ouvinte do deep link. Um por vez: sem isto, cada tentativa de login empilharia mais um. */
let ouvinteDeVolta: { remove: () => Promise<void> } | null = null;
/** O deep link chegou? Guarda o `browserFinished` de acusar falha num login que deu certo. */
let voltou = false;

async function pararEscuta(): Promise<void> {
  const h = ouvinteDeVolta;
  ouvinteDeVolta = null;
  if (h) await h.remove();
}

/**
 * Volta do Chrome: troca o `code` por uma sessão de verdade. O verificador do PKCE ficou guardado
 * aqui na WebView (a aba do Chrome é outro contexto), então a troca precisa acontecer DESTE lado.
 */
async function concluirLogin(urlDeVolta: string, Browser: { close: () => Promise<void> }): Promise<void> {
  if (!urlDeVolta.startsWith(DEEP_LINK)) return; // outro deep link qualquer: não é comigo
  voltou = true; // antes de fechar a aba: fechar dispara `browserFinished`
  await pararEscuta();
  try {
    await Browser.close();
  } catch {
    /* em alguns aparelhos a aba já fechou sozinha */
  }
  try {
    const u = new URL(urlDeVolta.replace("voidhunter://", "https://"));
    const q = u.searchParams;
    // MEDIDO: este cliente usa o fluxo IMPLICITO (a URL de autorizacao sai sem `code_challenge`),
    // entao a volta traz os tokens no FRAGMENTO, nao um `code` na query. Tratar os dois deixa o
    // login funcionando hoje e continua funcionando se um dia o cliente passar pra PKCE.
    const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
    const erro = q.get("error_description") || q.get("error") || frag.get("error_description") || frag.get("error");
    if (erro) {
      window.dispatchEvent(new CustomEvent("vh-auth", { detail: { ok: false, msg: traduz(erro) } }));
      return;
    }
    const code = q.get("code");
    const at = frag.get("access_token");
    const rt = frag.get("refresh_token");
    let falha: string | null = null;
    if (code) {
      falha = (await supabase.auth.exchangeCodeForSession(code)).error?.message ?? null;
    } else if (at && rt) {
      falha = (await supabase.auth.setSession({ access_token: at, refresh_token: rt })).error?.message ?? null;
    } else {
      return; // deep link sem nada util: ignora em silencio
    }
    if (falha) {
      window.dispatchEvent(new CustomEvent("vh-auth", { detail: { ok: false, msg: traduz(falha) } }));
      return;
    }
    window.dispatchEvent(new CustomEvent("vh-auth", { detail: { ok: true, msg: "Conta conectada!" } }));
    // Recarrega pra puxar o save da conta — mesmo caminho do login por e-mail.
    window.setTimeout(() => location.reload(), 900);
  } catch (err) {
    window.dispatchEvent(new CustomEvent("vh-auth", { detail: { ok: false, msg: String(err) } }));
  }
}

export async function logout(): Promise<void> {
  try {
    await supabase.auth.signOut();
  } catch {
    /* ignora */
  }
}

/** Mensagens comuns do Supabase em pt-BR (as demais passam cruas). */
function traduz(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("invalid login")) return "E-mail ou senha incorretos.";
  if (m.includes("already registered") || m.includes("already been registered")) return "Esse e-mail já tem conta. Use Entrar.";
  if (m.includes("email not confirmed")) return "Confirme seu e-mail antes de entrar.";
  if (m.includes("password should be")) return "A senha precisa de 6+ caracteres.";
  // 10.9IC — erros típicos de provedor social ainda não configurado no painel do Supabase
  if (m.includes("provider is not enabled") || m.includes("unsupported provider"))
    return "Esse login ainda não está ligado no servidor.";
  // 10.9IC — DOIS casos diferentes que eu tinha juntado numa mensagem só (e a errada aparecia):
  //  · "manual linking is disabled" = falta LIGAR no painel do Supabase (Auth > provedores)
  //  · "identity already exists"    = a conta social já pertence a outro jogador
  if (m.includes("manual linking") || m.includes("manual_linking"))
    return "Vincular conta social está desligado no servidor (falta ligar no painel).";
  if (jaVinculada(m))
    return "Essa conta já pertence a outro Caçador e não deu pra entrar nela. Tente de novo.";
  if (m.includes("redirect")) return "Endereço de retorno não autorizado no servidor.";
  return msg;
}
